import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/apiBase";
import { getGoogleClientId } from "../config/googleClientId";
import { postJSON } from "../services/api";
import "./EnrollmentPage.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const GOOGLE_CANCELLED_MESSAGE = "Google sign-in was not completed. You can try again or use the form below.";

function buildManualErrors({ fullName, email, password, confirmPassword }) {
  const errors = {};
  const trimmedFullName = String(fullName || "").trim();
  const trimmedEmail = String(email || "").trim().toLowerCase();

  if (!trimmedFullName) {
    errors.fullName = "Full Name is required.";
  }

  if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
    errors.email = "Please enter a valid email address.";
  }

  if (!String(password || "").trim()) {
    errors.password = "Please create a HIRI password.";
  } else if (String(password || "").length < 8) {
    errors.password = "Your HIRI password must contain at least 8 characters.";
  }

  if (!String(confirmPassword || "").trim()) {
    errors.confirmPassword = "Please confirm your HIRI password.";
  } else if (String(password || "") && String(confirmPassword || "") && password !== confirmPassword) {
    errors.confirmPassword = "The HIRI passwords do not match.";
  }

  return errors;
}

function mapServerErrorToField(message) {
  const text = String(message || "").toLowerCase();
  if (text.includes("full name")) return "fullName";
  if (text.includes("email")) return "email";
  if (text.includes("confirm")) return "confirmPassword";
  if (text.includes("password")) return "password";
  return "";
}

function SuccessPanel() {
  return (
    <section className="ep-success" aria-live="polite">
      <h2 className="ep-success-title">Request received</h2>
      <p className="ep-success-lead">Your enrolment request is pending review.</p>
      <p className="ep-success-copy">
        We will review it and contact you with the next steps.
      </p>
    </section>
  );
}

export default function EnrollmentPage() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const googleButtonRef = useRef(null);
  const googleInitializedRef = useRef(false);
  const googleRenderedRef = useRef(false);
  const googleCredentialHandlerRef = useRef(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [googleError, setGoogleError] = useState("");

  useEffect(() => {
    if (!attemptedSubmit) return;
    setValidationErrors(buildManualErrors({ fullName, email, password, confirmPassword }));
  }, [attemptedSubmit, fullName, email, password, confirmPassword]);

  const resetManualForm = () => {
    setFullName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setAttemptedSubmit(false);
    setValidationErrors({});
    setSubmitError("");
  };

  const finishSuccess = () => {
    setSubmitted(true);
    setSubmitError("");
    setGoogleError("");
    resetManualForm();
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const handleManualSubmit = async (event) => {
    event.preventDefault();
    setAttemptedSubmit(true);
    setGoogleError("");

    const errors = buildManualErrors({ fullName, email, password, confirmPassword });
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setLoading(true);
      const payload = await postJSON("/api/enrollments", {
        auth_method: "password",
        full_name: fullName.trim(),
        email: email.trim(),
        password,
      });
      if (payload?.success) {
        finishSuccess();
      }
    } catch (err) {
      const message = String(err?.message || "Enrollment request failed.");
      const field = mapServerErrorToField(message);
      if (field) {
        setValidationErrors((current) => ({ ...current, [field]: message }));
      } else {
        setSubmitError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitGoogleEnrollment = async (credential) => {
    const idToken = String(credential || "").trim();
    if (!idToken) {
      setGoogleError("Google sign-in did not return a credential.");
      return;
    }

    setSubmitError("");
    setGoogleError("");

    try {
      const authRes = await fetch(`${API_BASE_URL}/api/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: idToken, purpose: "enrollment" }),
      });
      const authPayload = await authRes.json().catch(() => ({}));
      if (!authRes.ok) {
        const detail = authPayload?.detail;
        const message =
          typeof detail === "string"
            ? detail
            : detail?.message || authPayload?.message || GOOGLE_CANCELLED_MESSAGE;
        throw new Error(message);
      }

      const googleEmail = String(authPayload?.email || "").trim().toLowerCase();
      const googleSub = String(authPayload?.google_sub || "").trim();
      if (!googleEmail || !googleSub) {
        throw new Error(GOOGLE_CANCELLED_MESSAGE);
      }

      await postJSON("/api/enrollments", {
        auth_method: "google",
        google_email: googleEmail,
        google_sub: googleSub,
        google_id_token: idToken,
      });
      finishSuccess();
    } catch (err) {
      const rawMessage = String(err?.message || "");
      const safeMessage = /token|oauth|credential|client id/i.test(rawMessage)
        ? GOOGLE_CANCELLED_MESSAGE
        : rawMessage || GOOGLE_CANCELLED_MESSAGE;
      setGoogleError(safeMessage);
    }
  };

  useEffect(() => {
    googleCredentialHandlerRef.current = (response) => {
      const credential = String(response?.credential || "").trim();
      void submitGoogleEnrollment(credential);
    };
  });

  useEffect(() => {
    const clientId = getGoogleClientId();
    const buttonContainer = googleButtonRef.current;
    if (!clientId || !buttonContainer) return undefined;

    let cancelled = false;

    const renderGoogleButton = () => {
      const google = window.google?.accounts?.id;
      if (cancelled || !google || !buttonContainer) return;

      if (!googleInitializedRef.current) {
        google.initialize({
          client_id: clientId,
          use_fedcm_for_prompt: false,
          callback: (response) => {
            googleCredentialHandlerRef.current?.(response);
          },
        });
        googleInitializedRef.current = true;
      }

      if (googleRenderedRef.current) return;
      const width = Math.max(300, Math.floor(buttonContainer.getBoundingClientRect().width || 0));
      buttonContainer.innerHTML = "";
      google.renderButton(buttonContainer, {
        theme: "filled_blue",
        size: "large",
        shape: "pill",
        text: "continue_with",
        logo_alignment: "left",
        width,
      });
      googleRenderedRef.current = true;
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return undefined;
    }

    const scriptId = "google-gsi-client";
    let script = document.getElementById(scriptId);
    const onLoad = () => renderGoogleButton();

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = onLoad;
      script.onerror = () => {
        if (!cancelled) {
          setGoogleError("Google sign-in could not load.");
        }
      };
      document.head.appendChild(script);
    } else {
      script.addEventListener("load", onLoad, { once: true });
    }

    return () => {
      cancelled = true;
      if (script) {
        script.removeEventListener?.("load", onLoad);
      }
      googleRenderedRef.current = false;
      if (buttonContainer) {
        buttonContainer.innerHTML = "";
      }
    };
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ep-page">
      <header className="ep-header">
        <div className="ep-container ep-header-inner">
          <a className="ep-brand" href="https://hiri.uk" aria-label="HIRI homepage">
            <img src={process.env.PUBLIC_URL + "/hirilogo.png"} alt="HIRI logo" className="ep-brand-logo" />
            <span className="ep-brand-copy">
              <span className="ep-brand-title">HIRI Creative Writing</span>
              <span className="ep-brand-subtitle">Guided creative writing</span>
            </span>
          </a>

          <nav className="ep-nav" aria-label="Primary">
            <a href="https://hiri.uk" className="ep-nav-link">
              Home
            </a>
            <a href="https://hiri.uk/#course" className="ep-nav-link">
              Course
            </a>
            <a href="https://hiri.uk/#about" className="ep-nav-link">
              About Shaanzae
            </a>
            <a href="https://hiri.uk/#sample-lesson" className="ep-nav-link">
              Sample Lesson
            </a>
            <a href="https://hiri.uk/#faq" className="ep-nav-link">
              FAQ
            </a>
            <a href="https://hiri.uk/#contact" className="ep-nav-link">
              Contact
            </a>
          </nav>

          <div className="ep-actions">
            <button type="button" className="ep-btn ep-btn-secondary" onClick={() => navigate("/login")}>
              Continue Lessons
            </button>
            <button type="button" className="ep-btn ep-btn-primary" onClick={scrollToForm}>
              Enrol Now
            </button>
          </div>
        </div>
      </header>

      <main className="ep-main">
        <section className="ep-hero">
          <div className="ep-container">
            <div className="ep-eyebrow">STUDENT ENROLMENT</div>
          </div>
        </section>

        <section className="ep-container ep-content">
          <div className="ep-info-grid">
            <article className="ep-info-card">
              <h2>Before you start</h2>
              <p>
                Manual enrolment uses a login name and password. Google enrolment uses your verified Google identity.
              </p>
            </article>
          </div>

          <section ref={formRef} className="ep-form-card">
            {submitted ? <SuccessPanel /> : null}

            <form className="ep-form" onSubmit={handleManualSubmit} noValidate>
              <div ref={googleButtonRef} className="ep-google-button-shell" aria-label="Continue with Google" />

              <div className="ep-google-divider" aria-hidden="true">
                <span>OR</span>
              </div>

              <div className="ep-grid">
                <label className="ep-field">
                  <span>Full Name</span>
                  <input
                    className="ep-input"
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                    type="text"
                    aria-label="Full Name"
                    autoComplete="name"
                  />
                  <span className="ep-help">This name will be used on the Completion Certificate</span>
                  {validationErrors.fullName ? <span className="ep-help ep-error">{validationErrors.fullName}</span> : null}
                </label>

                <label className="ep-field">
                  <span>Password</span>
                  <input
                    className="ep-input"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    type="password"
                    aria-label="Password"
                    autoComplete="new-password"
                  />
                  {validationErrors.password ? <span className="ep-help ep-error">{validationErrors.password}</span> : null}
                </label>

                <label className="ep-field">
                  <span>Confirm password</span>
                  <input
                    className="ep-input"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    type="password"
                    aria-label="Confirm password"
                    autoComplete="new-password"
                  />
                  {validationErrors.confirmPassword ? (
                    <span className="ep-help ep-error">{validationErrors.confirmPassword}</span>
                  ) : null}
                </label>

                <label className="ep-field">
                  <span>Email</span>
                  <input
                    className="ep-input"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    type="email"
                    aria-label="Email"
                    autoComplete="email"
                  />
                  {validationErrors.email ? <span className="ep-help ep-error">{validationErrors.email}</span> : null}
                </label>
              </div>

              {submitError ? <p className="ep-message ep-error">{submitError}</p> : null}

              <button type="submit" className="ep-btn ep-btn-primary ep-submit" disabled={loading}>
                {loading ? "Submitting..." : "Request Enrolment"}
              </button>

              {googleError ? <p className="ep-message ep-error">{googleError}</p> : null}
            </form>
          </section>
        </section>
      </main>

      <footer className="ep-footer">
        <div className="ep-container ep-footer-inner">
          <div className="ep-footer-brand">
            <img src={process.env.PUBLIC_URL + "/hirilogo.png"} alt="HIRI logo" className="ep-footer-logo" />
            <div>
              <div className="ep-footer-title">HIRI Creative Writing</div>
              <div className="ep-footer-copy">
                A public-facing marketing website for a guided creative writing course. The student app lives separately
                at app.hiri.uk.
              </div>
            </div>
          </div>

          <div className="ep-footer-links">
            <a href="https://hiri.uk">Home</a>
            <a href="https://hiri.uk/#course">Course</a>
            <a href="https://hiri.uk/#about">About Shaanzae</a>
            <a href="https://hiri.uk/#sample-lesson">Sample Lesson</a>
            <a href="https://hiri.uk/#faq">FAQ</a>
            <a href="https://hiri.uk/#contact">Contact</a>
          </div>

          <small className="ep-footer-small">Copyright 2026 HIRI Creative Writing. All rights reserved.</small>
        </div>
      </footer>
    </div>
  );
}
